/**
 * What the Bookkeeping screen SAYS about a batch of decisions, as pure functions.
 *
 * Extracted from `Bookkeeping.tsx` so it can be tested without rendering anything, the same reason
 * and the same shape as `control-center-view.ts` (ROADMAP section 7).
 *
 * There is one invariant here worth more than either function on its own: **the summary sentence
 * and the per-row labels must bucket a result the same way.** They are two descriptions of one
 * outcome, shown a centimetre apart, and a reader who finds them disagreeing cannot tell which to
 * believe. `bucketOf` is the single place that decides, and both read it.
 */

import type { BulkDecisionResult } from '../../api.js';

/**
 * Which outcome a decision had.
 *
 * `skipped` is exactly the 409s and 404s, which decided nothing. An approval the executor refused
 * comes back `approved` with `executed: false` at HTTP 200 rather than at 502, so "did not
 * execute" is read from the row's own state and never from its status code.
 */
export type DecisionBucket = 'executed' | 'notRun' | 'rejected' | 'skipped' | 'other';

export function bucketOf(r: BulkDecisionResult): DecisionBucket {
  if (r.httpStatus === 409 || r.httpStatus === 404) return 'skipped';
  if (r.status === 'rejected') return 'rejected';
  if (r.status === 'approved') return r.executed ? 'executed' : 'notRun';
  // `pending` or `unknown` at 200 would be a contract break. It is shown as stored, never
  // relabelled into a bucket it does not belong to.
  return 'other';
}

export function decisionLabel(r: BulkDecisionResult): {
  text: string;
  tone: string;
  error?: string;
} {
  switch (bucketOf(r)) {
    case 'skipped':
      return r.httpStatus === 409
        ? { text: 'Already decided', tone: 'status' }
        : { text: 'Not found', tone: 'status status-conflict' };
    case 'rejected':
      return { text: 'Rejected', tone: 'status' };
    case 'executed':
      return { text: 'Approved · executed', tone: 'status status-approved' };
    case 'notRun':
      return {
        text: 'Approved · did not execute',
        tone: 'status status-conflict',
        error: r.error ?? 'No reason was given.',
      };
    default:
      return { text: r.status, tone: 'status', ...(r.error ? { error: r.error } : {}) };
  }
}

export interface BatchCounts {
  executed: number;
  notRun: number;
  rejected: number;
  skipped: number;
}

export function countBatch(results: readonly BulkDecisionResult[]): BatchCounts {
  const counts: BatchCounts = { executed: 0, notRun: 0, rejected: 0, skipped: 0 };
  for (const r of results) {
    const bucket = bucketOf(r);
    if (bucket !== 'other') counts[bucket] += 1;
  }
  return counts;
}

/**
 * The sentence under the header, or null when the last batch is empty.
 *
 * `labelledOnScreen` is how many rows STILL RENDERED carry a label from this batch. A row that was
 * approved and executed leaves the queue on the refetch a moment later, so before this was
 * counted, "Each row says which" pointed at nothing for the commonest outcome there is
 * (walkthrough, 2026-09-10). The counts are the durable record and are always shown; only the
 * pointer is conditional, because only the pointer sends a reader looking.
 */
export function batchSentence(
  results: readonly BulkDecisionResult[],
  labelledOnScreen: number,
): string | null {
  if (results.length === 0) return null;
  const { executed, notRun, rejected, skipped } = countBatch(results);

  let text = `Last batch: ${executed} approved and executed, ${rejected} rejected`;
  if (notRun > 0) text += `, ${notRun} approved but NOT executed`;
  if (skipped > 0) text += `, ${skipped} already decided or not found`;
  text += '.';
  if (labelledOnScreen > 0) text += ' Each row says which.';
  return text;
}
